import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Chip,
  IconButton, Tooltip, Alert, CircularProgress, Switch, FormControlLabel, MenuItem
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';

const EMPTY = {
  ruc: '', razon_social: '', nombre_comercial: '', direccion: '',
  telefono: '', email: '', moneda_base: 'PEN',
  longitud_cuenta_min: 6, longitud_cuenta_max: 10,
  usa_centro_costo: false, usa_multimoneda: false,
  contabilidad_auto: false, activo: true,
  plan_costos: 'CLASE6', cuenta_costo_79_id: null,
  sunat_client_id: '', sunat_client_secret: '', sunat_usuario_sol: '', sunat_clave_sol: ''
};

export default function EmpresasContables() {
  const esAdmin = (() => {
    try { return JSON.parse(localStorage.getItem('usuario') || 'null')?.perfil_id === 1; }
    catch { return false; }
  })();

  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [buscandoRuc, setBuscandoRuc] = useState(false);
  const [eliminando, setEliminando] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cuentas79, setCuentas79] = useState([]);
  const [c79InputVal, setC79InputVal] = useState('');
  // Objeto de la cuenta 79 SELECCIONADA, independiente de `cuentas79` (resultados de
  // búsqueda) -- antes el <Autocomplete value> se derivaba con .find() dentro de
  // `cuentas79`, así que apenas el usuario escribía algo que ya no traía la cuenta
  // elegida entre los primeros 30 resultados, value colapsaba a null a mitad de tipeo.
  const [cuenta79Sel, setCuenta79Sel] = useState(null);
  const c79BuscarTimer = useRef(null);
  const c79BuscarSeq = useRef(0);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/contabilidad/empresas');
      setEmpresas(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError('Error al cargar empresas contables');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNueva = () => {
    setEditando(null);
    setForm(EMPTY);
    setCuenta79Sel(null);
    setC79InputVal('');
    setError('');
    setOpen(true);
  };

  // Debounce (300ms) + protección contra respuestas fuera de orden -- sin esto, cada
  // tecla disparaba una búsqueda nueva y una respuesta vieja podía llegar después de una
  // más nueva, pisando los resultados correctos con los de un término ya superado.
  const buscarCuentas79Inmediato = async (empresaId, termino) => {
    if (!empresaId) return;
    const miSeq = ++c79BuscarSeq.current;
    try {
      const r = await axios.get('/api/contabilidad/plan', {
        params: { empresa_id: empresaId, buscar: termino || '79', solo_hojas: '1' }
      });
      if (miSeq !== c79BuscarSeq.current) return;
      setCuentas79(Array.isArray(r.data) ? r.data.slice(0, 30) : []);
    } catch (_) {}
  };

  const buscarCuentas79 = (empresaId, termino) => {
    if (c79BuscarTimer.current) clearTimeout(c79BuscarTimer.current);
    c79BuscarTimer.current = setTimeout(() => buscarCuentas79Inmediato(empresaId, termino), 300);
  };

  const abrirEditar = (emp) => {
    setEditando(emp);
    setForm({
      ruc: emp.ruc,
      razon_social: emp.razon_social,
      nombre_comercial: emp.nombre_comercial || '',
      direccion: emp.direccion || '',
      telefono: emp.telefono || '',
      email: emp.email || '',
      moneda_base: emp.moneda_base || 'PEN',
      longitud_cuenta_min: emp.longitud_cuenta_min || 6,
      longitud_cuenta_max: emp.longitud_cuenta_max || 10,
      usa_centro_costo: !!emp.usa_centro_costo,
      usa_multimoneda: !!emp.usa_multimoneda,
      contabilidad_auto: !!emp.contabilidad_auto,
      activo: emp.activo !== 0,
      plan_costos: emp.plan_costos || 'CLASE6',
      cuenta_costo_79_id: emp.cuenta_costo_79_id || null,
      sunat_client_id: emp.sunat_client_id || '',
      sunat_client_secret: emp.sunat_client_secret || '',
      sunat_usuario_sol: emp.sunat_usuario_sol || '',
      sunat_clave_sol: ''
    });
    setC79InputVal(emp.cuenta_79_codigo ? `${emp.cuenta_79_codigo} - ${emp.cuenta_79_descripcion}` : '');
    setCuenta79Sel(emp.cuenta_costo_79_id
      ? { id: emp.cuenta_costo_79_id, codigo: emp.cuenta_79_codigo, descripcion: emp.cuenta_79_descripcion }
      : null);
    buscarCuentas79Inmediato(emp.id, '79');
    setError('');
    setOpen(true);
  };

  const confirmarEliminar = async () => {
    if (!eliminando) return;
    setSaving(true);
    try {
      await axios.delete(`/api/contabilidad/empresas/${eliminando.id}`);
      setSuccess(`Empresa "${eliminando.razon_social}" eliminada correctamente`);
      setEliminando(null);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setEliminando(null);
    } finally {
      setSaving(false);
    }
  };

  const buscarRuc = async () => {
    const ruc = form.ruc?.trim();
    if (!ruc || ruc.length !== 11) { setError('Ingrese un RUC de 11 dígitos para consultar'); return; }
    setBuscandoRuc(true);
    setError('');
    try {
      const r = await axios.get(`/api/consulta-peru/ruc/${ruc}`);
      if (r.data?.success) {
        setForm(f => ({
          ...f,
          razon_social:    r.data.razonSocial    || f.razon_social,
          nombre_comercial: r.data.nombreComercial || r.data.razonSocial || f.nombre_comercial,
          direccion:        r.data.direccion     || f.direccion,
        }));
      } else {
        setError(r.data?.message || 'RUC no encontrado en SUNAT');
      }
    } catch (e) {
      setError('Error al consultar SUNAT: ' + (e.response?.data?.error || e.message));
    } finally {
      setBuscandoRuc(false);
    }
  };

  const guardar = async () => {
    if (!form.ruc || !form.razon_social) {
      setError('RUC y Razón Social son requeridos');
      return;
    }
    if (Number(form.longitud_cuenta_min) < 6) {
      setError('La longitud mínima de cuenta no puede ser menor a 6');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        usa_centro_costo: form.usa_centro_costo ? 1 : 0,
        usa_multimoneda: form.usa_multimoneda ? 1 : 0,
        contabilidad_auto: form.contabilidad_auto ? 1 : 0,
        activo: form.activo ? 1 : 0
      };

      let data;
      if (editando) {
        const r = await axios.put(`/api/contabilidad/empresas/${editando.id}`, body);
        data = r.data;
      } else {
        const r = await axios.post('/api/contabilidad/empresas', body);
        data = r.data;
      }

      setSuccess(editando ? 'Empresa actualizada correctamente' : `Empresa creada. PCGE Perú cargado automáticamente. ID: ${data.id}`);
      setOpen(false);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <BusinessIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" fontWeight="bold">Empresas Contables</Typography>
        <Box sx={{ flex: 1 }} />
        {esAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNueva}>
            Nueva Empresa
          </Button>
        )}
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && !open && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>RUC</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Razón Social</TableCell>
                <TableCell className="cont-col-md" sx={{ color: 'white', fontWeight: 'bold' }}>Moneda</TableCell>
                <TableCell className="cont-col-md" sx={{ color: 'white', fontWeight: 'bold' }}>Cta. Min</TableCell>
                <TableCell className="cont-col-md" sx={{ color: 'white', fontWeight: 'bold' }}>Cta. Max</TableCell>
                <TableCell className="cont-col-md" sx={{ color: 'white', fontWeight: 'bold' }}>Centro Costo</TableCell>
                <TableCell className="cont-col-md" sx={{ color: 'white', fontWeight: 'bold' }}>Auto Asientos</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Estado</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {empresas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No hay empresas contables registradas. Cree la primera.
                  </TableCell>
                </TableRow>
              ) : empresas.map(e => (
                <TableRow key={e.id} hover>
                  <TableCell>{e.ruc}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{e.razon_social}</Typography>
                    {e.nombre_comercial && (
                      <Typography variant="caption" color="text.secondary">{e.nombre_comercial}</Typography>
                    )}
                  </TableCell>
                  <TableCell className="cont-col-md"><Chip label={e.moneda_base} size="small" /></TableCell>
                  <TableCell className="cont-col-md" align="center">{e.longitud_cuenta_min}</TableCell>
                  <TableCell className="cont-col-md" align="center">{e.longitud_cuenta_max}</TableCell>
                  <TableCell className="cont-col-md" align="center">
                    {e.usa_centro_costo ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="disabled" fontSize="small" />}
                  </TableCell>
                  <TableCell className="cont-col-md" align="center">
                    {e.contabilidad_auto ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="disabled" fontSize="small" />}
                  </TableCell>
                  <TableCell>
                    <Chip label={e.activo ? 'Activo' : 'Inactivo'} color={e.activo ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    {esAdmin ? (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEditar(e)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar empresa y todos sus datos contables">
                          <IconButton size="small" color="error" onClick={() => setEliminando(e)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Editar (solo lectura)">
                        <span>
                          <IconButton size="small" disabled>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editando ? `Editar: ${editando.razon_social}` : 'Nueva Empresa Contable'}
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {!editando && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Al crear la empresa se cargará automáticamente el <strong>PCGE Perú 2024</strong> completo como plan de cuentas inicial.
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField label="RUC" name="ruc" value={form.ruc} onChange={handleChange}
                  fullWidth size="small" required disabled={!!editando}
                  inputProps={{ maxLength: 11 }} />
                {!editando && (
                  <Tooltip title="Consultar RUC en SUNAT">
                    <span>
                      <IconButton size="small" onClick={buscarRuc} disabled={buscandoRuc || form.ruc?.length !== 11}
                        color="primary" sx={{ mt: 0.5 }}>
                        {buscandoRuc ? <CircularProgress size={18} /> : <SearchIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Razón Social" name="razon_social" value={form.razon_social}
                onChange={handleChange} fullWidth size="small" required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Nombre Comercial" name="nombre_comercial" value={form.nombre_comercial}
                onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email" name="email" type="email" value={form.email}
                onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Dirección" name="direccion" value={form.direccion}
                onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Teléfono" name="telefono" value={form.telefono}
                onChange={handleChange} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Moneda Base" name="moneda_base" value={form.moneda_base}
                onChange={handleChange} fullWidth size="small"
                helperText="PEN, USD, EUR" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Longitud mín. cuenta analítica" name="longitud_cuenta_min"
                type="number" value={form.longitud_cuenta_min} onChange={handleChange}
                fullWidth size="small" inputProps={{ min: 6, max: 10 }}
                helperText="Mínimo 6 dígitos (PCGE)" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Longitud máx. cuenta" name="longitud_cuenta_max"
                type="number" value={form.longitud_cuenta_max} onChange={handleChange}
                fullWidth size="small" inputProps={{ min: 6, max: 12 }}
                helperText="Hasta 12 dígitos" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch name="usa_centro_costo" checked={form.usa_centro_costo} onChange={handleChange} />
              } label="Centro de costo" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch name="usa_multimoneda" checked={form.usa_multimoneda} onChange={handleChange} />
              } label="Multimoneda" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel control={
                <Switch name="contabilidad_auto" checked={form.contabilidad_auto} onChange={handleChange} />
              } label="Asientos automáticos desde ERP" />
            </Grid>
            {editando && (
              <Grid item xs={12} sm={4}>
                <FormControlLabel control={
                  <Switch name="activo" checked={form.activo} onChange={handleChange} />
                } label="Activo" />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField label="Plan de Costos" name="plan_costos" select value={form.plan_costos}
                onChange={handleChange} fullWidth size="small" sx={{ minWidth: 170 }}
                helperText="Clase en la que se ingresan costos/gastos directos">
                <MenuItem value="CLASE6">CLASE 6 (Ingresar clase 6, destino clase 9)</MenuItem>
                <MenuItem value="CLASE9">CLASE 9 (Ingresar clase 9, destino clase 6)</MenuItem>
              </TextField>
            </Grid>
            {editando && (
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  options={cuentas79}
                  getOptionLabel={o => `${o.codigo} - ${o.descripcion}`}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={cuenta79Sel}
                  inputValue={c79InputVal}
                  onInputChange={(_, val, reason) => {
                    setC79InputVal(val);
                    // MUI dispara onInputChange con reason='reset' justo después de
                    // seleccionar (texto completo de la opción elegida) -- solo buscamos
                    // cuando el usuario realmente está escribiendo (reason === 'input').
                    // El value ya NO depende de "cuentas79" (ver cuenta79Sel arriba), así
                    // que aunque la búsqueda no traiga de vuelta la cuenta elegida, el
                    // campo no se queda en blanco ni se corta mientras se escribe.
                    if (reason === 'input') {
                      buscarCuentas79(editando?.id, val);
                    }
                  }}
                  onFocus={() => { if (cuentas79.length === 0) buscarCuentas79Inmediato(editando?.id, c79InputVal); }}
                  onChange={(_, val) => {
                    setCuenta79Sel(val);
                    setForm(f => ({ ...f, cuenta_costo_79_id: val?.id || null }));
                    setC79InputVal(val ? `${val.codigo} - ${val.descripcion}` : '');
                  }}
                  renderInput={params => (
                    <TextField {...params} label="Cuenta 79 - Cargas imputables a costos" size="small"
                      helperText="Cuenta nexo entre clase 6 y clase 9 (79xxxx)" />
                  )}
                  renderOption={(props, o) => (
                    <li {...props} key={o.id}>
                      <span style={{ fontFamily: 'monospace', color: '#388e3c', marginRight: 8 }}>{o.codigo}</span>
                      {o.descripcion}
                    </li>
                  )}
                  noOptionsText="Sin resultados"
                  size="small"
                />
              </Grid>
            )}
          {/* ── Configuración API SUNAT ── */}
          <Grid item xs={12}>
            <Box sx={{ mt: 1, p: 2, bgcolor: '#f0f7ff', borderRadius: 2, border: '1px solid #bbdefb' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={1}>
                Configuración API SUNAT (SIRE / GRE)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                Mismo Client ID y Client Secret del portal SUNAT que usas para Guía de Remisión, y el
                Usuario y Clave del usuario secundario SOL — se guardan para no tener que ingresarlos
                en cada importación SIRE.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField label="Client ID (SUNAT)" name="sunat_client_id" value={form.sunat_client_id}
                    onChange={handleChange} fullWidth size="small"
                    placeholder="ej: 123456789012" />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField label="Client Secret (SUNAT)" name="sunat_client_secret" value={form.sunat_client_secret}
                    onChange={handleChange} fullWidth size="small" type="password"
                    placeholder="••••••••••••••••" />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField label="Usuario SOL (secundario)" name="sunat_usuario_sol" value={form.sunat_usuario_sol}
                    onChange={handleChange} fullWidth size="small"
                    placeholder="ej: USERSOL01 (sin RUC)" />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField label="Clave SOL (secundario)" name="sunat_clave_sol" value={form.sunat_clave_sol}
                    onChange={handleChange} fullWidth size="small" type="password"
                    placeholder={editando?.sunat_clave_sol_configurada ? 'Ya guardada — déjalo en blanco para mantenerla' : '••••••••••••••••'}
                    helperText={editando?.sunat_clave_sol_configurada ? 'Ya hay una clave guardada' : ' '} />
                </Grid>
              </Grid>
            </Box>
          </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editando ? 'Guardar cambios' : 'Crear empresa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmación eliminar */}
      <Dialog open={!!eliminando} onClose={() => setEliminando(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" /> Eliminar Empresa Contable
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Esta acción es irreversible.</strong>
          </Alert>
          <Typography>
            Se eliminará permanentemente la empresa <strong>"{eliminando?.razon_social}"</strong> (RUC: {eliminando?.ruc}) junto con:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Plan de cuentas completo</li>
            <li>Todos los períodos contables</li>
            <li>Parámetros y configuración</li>
            <li>Logs de importación</li>
          </Box>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Solo se puede eliminar si la empresa NO tiene asientos contables registrados.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminando(null)} disabled={saving}>Cancelar</Button>
          <Button onClick={confirmarEliminar} variant="contained" color="error" disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : <DeleteIcon />}>
            Sí, eliminar empresa
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
